import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from 'axios';
import PropTypes from 'prop-types';
import { createAppTheme } from '../utils/themeUtils';
import {
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Container,
  Card,
  CardContent,
  CircularProgress,
  ThemeProvider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider, // Imported for AudioPlayer
  IconButton // Imported for AudioPlayer
} from "@mui/material";
import { styled } from '@mui/system';
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

/**
 * AudioPlayer Component
 * ----------------------
 * This component is responsible for rendering an audio player that allows users to play, pause, seek, and view the playback progress of audio files.
 * It uses the Web Audio API (`AudioContext`) to decode and play audio files fetched from provided URLs.
 */
const AudioPlayer = ({ audioUrls }) => {
  const [audioBuffers, setAudioBuffers] = useState([]);
  const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [errorMessages, setErrorMessages] = useState([]); // Track errors for each file

  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const startTimeRef = useRef(0);
  const intervalRef = useRef(null);

  const normalizeAudioUrl = useCallback((url) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('/api/')) return url;
    if (url.startsWith('/audio/')) return `/api${url}`;
    return url;
  }, []);

  // Fetch and decode audio files when the component mounts
  useEffect(() => {
    const fetchAudioFiles = async () => {
      if (!audioUrls || audioUrls.length === 0) return;

      try {
        const context = new AudioContext();
        audioContextRef.current = context;

        // Fetch and decode audio files into AudioBuffer objects
        const buffers = await Promise.all(
          audioUrls.map(async (url, index) => {
            try {
              const normalizedUrl = normalizeAudioUrl(url);
              const response = await fetch(normalizedUrl);
              if (!response.ok) {
                throw new Error(`Failed to fetch audio file: ${normalizedUrl}`);
              }
              const arrayBuffer = await response.arrayBuffer();
              return await context.decodeAudioData(arrayBuffer);
            } catch (error) {
              console.error(`Error loading audio file: ${url}`, error);
              setErrorMessages((prev) => {
                const updatedErrors = [...prev];
                updatedErrors[index] = "File not found."; // Set error message for this file
                return updatedErrors;
              });
              return null; // Return null for files that fail to load
            }
          })
        );

        // Filter out any null values (failed files) and set the audio buffers
        const validBuffers = buffers.filter((buffer) => buffer !== null);
        if (validBuffers.length === 0) {
          console.error("No valid audio files found.");
          return;
        }

        setAudioBuffers(validBuffers);
        setDuration(validBuffers[0]?.duration || 0); // Set duration for the first valid audio file
      } catch (error) {
        console.error("Error initializing audio context or loading files:", error);
      }
    };

    fetchAudioFiles();

    // Cleanup: Close the AudioContext and clear intervals on unmount
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      clearInterval(intervalRef.current);
    };
  }, [audioUrls, normalizeAudioUrl]);

  // Play the audio from the current position
  const playAudio = () => {
    if (!audioBuffers[currentAudioIndex]) return;

    const context = audioContextRef.current;
    const source = context.createBufferSource();
    source.buffer = audioBuffers[currentAudioIndex];
    source.connect(context.destination);

    const offset = currentTime; // Start playback from the current time
    source.start(0, offset);

    sourceRef.current = source;
    startTimeRef.current = context.currentTime - offset; // Track the start time of playback
    setIsPlaying(true);

    // Update progress periodically
    intervalRef.current = setInterval(() => {
      const elapsedTime = context.currentTime - startTimeRef.current; // Calculate elapsed playback time

      // Check if the audio has finished playing
      if (elapsedTime >= audioBuffers[currentAudioIndex].duration) {
        clearInterval(intervalRef.current); // Stop the interval when playback is complete
        setIsPlaying(false);
        setProgress(100); // Set progress to 100% as the audio has finished
        setCurrentTime(audioBuffers[currentAudioIndex].duration); // Set current time to the total duration
      } else {
        // Update progress and current time while the audio is playing
        setProgress((elapsedTime / audioBuffers[currentAudioIndex].duration) * 100); // Calculate progress as a percentage
        setCurrentTime(elapsedTime);
      }
    }, 500); // Update every 500ms to keep the slider and time display in sync
  };

  // Pause the audio and stop progress updates
  const pauseAudio = () => {
    if (sourceRef.current) {
      sourceRef.current.stop();
      sourceRef.current = null;
    }
    setIsPlaying(false);
    clearInterval(intervalRef.current);
  };

  // Handle slider movement to seek to a new position
  const handleSeek = (event, newValue) => {
    const context = audioContextRef.current;

    if (!audioBuffers[currentAudioIndex]) return;

    const newTime = (newValue / 100) * audioBuffers[currentAudioIndex].duration;
    setProgress(newValue);
    setCurrentTime(newTime);

    // Pause the audio when the slider is moved
    if (isPlaying) {
      if (sourceRef.current) {
        sourceRef.current.stop();
        sourceRef.current = null;
      }

      const source = context.createBufferSource();
      source.buffer = audioBuffers[currentAudioIndex];
      source.connect(context.destination);
      source.start(0, newTime);

      sourceRef.current = source;
      startTimeRef.current = context.currentTime - newTime;
    }
  };

  // Handle slider release to resume playback if paused
  const handleSeekCommitted = (event, newValue) => {
    const newTime = (newValue / 100) * audioBuffers[currentAudioIndex].duration;
    setCurrentTime(newTime);
    setProgress(newValue);

    if (!isPlaying) {
      playAudio(); // Resume playback if paused
    }
  };

  // Format time in MM:SS format
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
      {audioUrls.map((url, index) => (
        <Box key={index} sx={{ mb: 2 }}>
          {errorMessages[index] ? (
            // Display error message if the file failed to load
            <Typography color="error">{errorMessages[index]}</Typography>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {/* Play/Pause Button */}
                <IconButton
                  onClick={isPlaying ? pauseAudio : playAudio}
                  color="primary"
                  size="large"
                >
                  {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
                {/* Slider with Playback Duration */}
                <Slider
                  value={progress}
                  onChange={handleSeek} // Update slider position immediately
                  onChangeCommitted={handleSeekCommitted} // Handle when the user releases the slider
                  aria-label="Progress"
                  sx={{
                    flex: 1, // Make the slider take up the remaining space
                    "& .MuiSlider-thumb": {
                      color: 'primary.main',
                    },
                    "& .MuiSlider-track": {
                      color: 'primary.main',
                    },
                    "& .MuiSlider-rail": {
                      color: "#ccc",
                    },
                  }}
                />
                {/* Playback Duration */}
                <Typography variant="body2" sx={{ minWidth: "80px", textAlign: "right" }}>
                  {formatTime(currentTime)} / {formatTime(duration)}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      ))}
    </Box>
  );
};

// Styled components for better visual hierarchy
const StyledListItem = styled(ListItem)(({ theme }) => ({
  transition: 'background-color 0.2s ease',
  backgroundColor: theme.palette.background.paper,
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));
//const ProfessorView = ({ flaskHOST, flaskPORT, themeMode = 'light' }) => {
  const ProfessorView = ({ themeMode = 'light', onThemeChange, initialView = 'students' }) => {
  const [email, setEmail] = useState('');
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedChatbot, setSelectedChatbot] = useState(null);
  const [selectedChatbotTitle, setSelectedChatbotTitle] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [displayOrder, setDisplayOrder] = useState('default');
  const [chatbotConversations, setChatbotConversations] = useState({}); // NEW
  const [chatbotStudentHistories, setChatbotStudentHistories] = useState([]);
  const [expandedStudentPanels, setExpandedStudentPanels] = useState({});
  const [isTopSelectionCollapsed, setIsTopSelectionCollapsed] = useState(false);
  const [chatbotNameByTitle, setChatbotNameByTitle] = useState({});
  const [chatbotLevelByTitle, setChatbotLevelByTitle] = useState({});
  const [studentTranscriptEmail, setStudentTranscriptEmail] = useState('');
  const [studentTranscriptSummaries, setStudentTranscriptSummaries] = useState([]);
  const [selectedTranscriptConversation, setSelectedTranscriptConversation] = useState([]);
  const [selectedTranscriptMeta, setSelectedTranscriptMeta] = useState(null);
  const [isTranscriptLookupLoading, setIsTranscriptLookupLoading] = useState(false);
  const topSelectionListRef = useRef(null);
  const selectionItemRefs = useRef({});
  const isLookupOnlyView = initialView === 'lookup';

  // Create theme based on the themeMode prop
  const theme = createAppTheme(themeMode);

  useEffect(() => {
    const fetchChatbotNames = async () => {
      try {
        const res = await axios.get('/api/data');
        const prompts = res?.data || {};
        const names = Object.entries(prompts).reduce((acc, [title, config]) => {
          const mappedName = (config?.name || '').trim();
          if (title && mappedName) {
            acc[title] = mappedName;
          }
          return acc;
        }, {});
        const levels = Object.entries(prompts).reduce((acc, [title, config]) => {
          const mappedLevel = (config?.level || '').trim();
          if (title && mappedLevel) {
            acc[title] = mappedLevel;
          }
          return acc;
        }, {});
        setChatbotNameByTitle(names);
        setChatbotLevelByTitle(levels);
      } catch {
        setChatbotNameByTitle({});
        setChatbotLevelByTitle({});
      }
    };

    fetchChatbotNames();
  }, []);

  const getChatbotDisplayName = useCallback(
    (chatbotTitle) => chatbotNameByTitle[chatbotTitle] || chatbotTitle,
    [chatbotNameByTitle]
  );

  const getChatbotDisplayLevel = useCallback(
    (chatbotTitle) => chatbotLevelByTitle[chatbotTitle] || '',
    [chatbotLevelByTitle]
  );

  const handleEmailSubmit = useCallback(async () => {
    if (!email) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }
    setIsLoading(true);
    setErrorMessage('');
    try {
      //const res = await axios.get(`https://${flaskHOST}:${flaskPORT}/professor/students?email=${encodeURIComponent(email)}`);
      console.log('DEBUG Todd: Fetching students for email:', email);
      const res = await axios.get(`/api/professor/students?email=${encodeURIComponent(email)}`);
      console.log('DEBUG Todd2: Fetching students for email:', email);
      setStudents(res.data.students || []);
      setSelectedStudent(null);
      setSelectedChatbot(null);
      setSelectedChatbotTitle(null);
      setConversation([]);
      setChatbotStudentHistories([]);
      setExpandedStudentPanels({});
      setIsTopSelectionCollapsed(true);
    } catch (e) {
      setErrorMessage('Error fetching student list. Please try again.');
    } finally {
      setIsLoading(false);
    }
  
  }, [email]);
  // }, [email, flaskHOST, flaskPORT]);

  const handleStudentClick = useCallback((student) => {
    setSelectedStudent(student);
    setSelectedChatbot(null);
    setSelectedChatbotTitle(null);
    setConversation([]);
    setChatbotStudentHistories([]);
    setExpandedStudentPanels({});
    setErrorMessage('');
  }, []);

  const handleDisplayOrderChange = useCallback((event) => {
    setDisplayOrder(event.target.value);
    setSelectedStudent(null);
    setSelectedChatbot(null);
    setSelectedChatbotTitle(null);
    setConversation([]);
    setChatbotStudentHistories([]);
    setExpandedStudentPanels({});
    setIsTopSelectionCollapsed(true);
    setErrorMessage('');
  }, []);

  const handleChatbotModeClick = useCallback(async (chatbotTitle) => {
    setErrorMessage('');
    setSelectedStudent(null);
    setSelectedChatbot(getChatbotDisplayName(chatbotTitle));
    setSelectedChatbotTitle(chatbotTitle);
    setConversation([]);

    const studentsUsingChatbot = students.filter((student) => Boolean(student?.chatbots?.[chatbotTitle]));

    try {
      const historyEntries = await Promise.all(
        studentsUsingChatbot.map(async (student) => {
          try {
            const res = await axios.get('/api/professor/conversation', {
              params: {
                prof_email: email,
                student_key: student.key,
                chatbot_name: chatbotTitle,
              },
            });

            return {
              studentName: student.name,
              studentEmail: student.email,
              studentKey: student.key,
              lastUsed: res?.data?.timestamp || student?.chatbots?.[chatbotTitle]?.last_used || '',
              conversation: Array.isArray(res?.data?.conversation) ? res.data.conversation : [],
            };
          } catch {
            return {
              studentName: student.name,
              studentEmail: student.email,
              studentKey: student.key,
              lastUsed: student?.chatbots?.[chatbotTitle]?.last_used || '',
              conversation: [],
            };
          }
        })
      );

      const sortedEntries = historyEntries.sort((a, b) => {
        const timeA = parseLastUsedToMillis(a.lastUsed);
        const timeB = parseLastUsedToMillis(b.lastUsed);
        if (timeB !== timeA) return timeB - timeA;
        return `${a.studentName}`.localeCompare(`${b.studentName}`);
      });

      setChatbotStudentHistories(sortedEntries);
      setExpandedStudentPanels({});

      if (sortedEntries.length === 0) {
        setErrorMessage(`No student history found for ${getChatbotDisplayName(chatbotTitle)}.`);
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch {
      setErrorMessage('Failed to load chatbot histories. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  }, [email, students, getChatbotDisplayName]);

  const handleStudentPanelToggle = useCallback((studentKey) => () => {
    setExpandedStudentPanels((prev) => ({
      ...prev,
      [studentKey]: !prev[studentKey],
    }));
  }, []);

  const handleStudentTranscriptLookup = useCallback(async () => {
    const normalizedEmail = (studentTranscriptEmail || '').trim().toLowerCase();
    if (!normalizedEmail) {
      setErrorMessage('Please enter a student email address for transcript lookup.');
      return;
    }

    setErrorMessage('');
    setIsTranscriptLookupLoading(true);
    setSelectedTranscriptConversation([]);
    setSelectedTranscriptMeta(null);

    console.info('[ProfessorLookup] Searching transcripts by student email', { student_email: normalizedEmail });

    try {
      const res = await axios.get('/api/professor/transcripts', {
        params: { student_email: normalizedEmail },
      });
      const transcripts = Array.isArray(res?.data?.transcripts) ? res.data.transcripts : [];
      console.info('[ProfessorLookup] Transcript search results', {
        student_email: normalizedEmail,
        conversation_count: transcripts.length,
      });
      setStudentTranscriptSummaries(transcripts);
    } catch {
      setStudentTranscriptSummaries([]);
      setErrorMessage('Failed to load transcripts for that student email.');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setIsTranscriptLookupLoading(false);
    }
  }, [studentTranscriptEmail]);

  const handleTranscriptConversationOpen = useCallback(async (transcriptSummary) => {
    const normalizedEmail = (studentTranscriptEmail || '').trim().toLowerCase();
    const userToken = transcriptSummary?.user_token;
    if (!normalizedEmail || !userToken) {
      return;
    }

    setErrorMessage('');
    setIsTranscriptLookupLoading(true);
    console.info('[ProfessorLookup] Opening conversation transcript', {
      student_email: normalizedEmail,
      user_token: userToken,
    });
    try {
      const res = await axios.get('/api/professor/transcript/conversation', {
        params: {
          student_email: normalizedEmail,
          user_token: userToken,
        },
      });

      setSelectedTranscriptConversation(Array.isArray(res?.data?.conversation) ? res.data.conversation : []);
      setSelectedTranscriptMeta({
        studentEmail: normalizedEmail,
        userToken,
        timestamp: transcriptSummary?.timestamp || res?.data?.timestamp || '',
        chatbotTitle: transcriptSummary?.chatbot_title || res?.data?.chatbot_title || '',
      });
    } catch {
      setSelectedTranscriptConversation([]);
      setSelectedTranscriptMeta(null);
      setErrorMessage('Failed to load that transcript conversation.');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setIsTranscriptLookupLoading(false);
    }
  }, [studentTranscriptEmail]);

  const handleChatbotClick = useCallback(async (chatbotTitle) => {
    setErrorMessage('');
    try {
      //const res = await axios.get(`https://${flaskHOST}:${flaskPORT}/professor/conversation`, {
        const res = await axios.get(`/api/professor/conversation`, {
        params: {
          prof_email: email,
          student_key: selectedStudent.key,
          chatbot_name: chatbotTitle,
        },
      });
      if (res.data.conversation && res.data.conversation.length > 0) {
        setSelectedChatbot(getChatbotDisplayName(chatbotTitle));
        setSelectedChatbotTitle(chatbotTitle);
        setConversation(res.data.conversation);

        // Keep local student list metadata in sync with newest server timestamp.
        const latestTimestamp = res.data.timestamp;
        if (latestTimestamp) {
          setSelectedStudent((prev) => {
            if (!prev?.chatbots?.[chatbotTitle]) return prev;
            return {
              ...prev,
              chatbots: {
                ...prev.chatbots,
                [chatbotTitle]: {
                  ...prev.chatbots[chatbotTitle],
                  last_used: latestTimestamp,
                },
              },
            };
          });

          setStudents((prevStudents) =>
            prevStudents.map((student) => {
              if (student.key !== selectedStudent.key || !student.chatbots?.[chatbotTitle]) {
                return student;
              }
              return {
                ...student,
                chatbots: {
                  ...student.chatbots,
                  [chatbotTitle]: {
                    ...student.chatbots[chatbotTitle],
                    last_used: latestTimestamp,
                  },
                },
              };
            })
          );
        }
      } else {
        setSelectedChatbot(null);
        setSelectedChatbotTitle(null);
        setConversation([]);
        setErrorMessage(`No conversation found for ${getChatbotDisplayName(chatbotTitle)}.`);
        setTimeout(() => setErrorMessage(''), 3000); // Clear message after 3s
      }
    } catch (e) {
      setSelectedChatbot(null);
      setSelectedChatbotTitle(null);
      setConversation([]);
      setErrorMessage('Failed to load conversation. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    }
    //}, [email, selectedStudent, flaskHOST, flaskPORT]);
  }, [email, selectedStudent, getChatbotDisplayName]);

  // Fetch all chatbot conversations when a student is selected
  useEffect(() => {
    if (selectedStudent && !selectedChatbot) {
      const fetchAllConversations = async () => {
        const chatbots = Object.keys(selectedStudent.chatbots || {});
        const conversationData = {};
        await Promise.all(chatbots.map(async (chatbotName) => {
          try {
            const res = await axios.get(`/api/professor/conversation`, {
              params: {
                prof_email: email,
                student_key: selectedStudent.key,
                chatbot_name: chatbotName,
              },
            });
            conversationData[chatbotName] = res.data.conversation || [];
          } catch {
            conversationData[chatbotName] = [];
          }
        }));
        setChatbotConversations(conversationData);
      };
      fetchAllConversations();
    } else {
      setChatbotConversations({});
    }
  }, [selectedStudent, selectedChatbot, email]);

  const getYearFromLastEdited = (lastUsedValue) => {
    if (!lastUsedValue) return null;

    const explicitYear = String(lastUsedValue).match(/\b(\d{4})\b/);
    if (explicitYear) return Number(explicitYear[1]);

    const nativeParsed = Date.parse(lastUsedValue);
    if (!Number.isNaN(nativeParsed)) {
      return new Date(nativeParsed).getFullYear();
    }

    return null;
  };

  const selectedChatbotLastUsed = selectedStudent?.chatbots?.[selectedChatbotTitle]?.last_used;
  const selectedChatbotYear = getYearFromLastEdited(selectedChatbotLastUsed);

  const defaultYearForTimestampParse = (() => {
    const years = students
      .flatMap((student) => Object.values(student?.chatbots || {}))
      .map((chatbot) => {
        const raw = String(chatbot?.last_used || '');
        const match = raw.match(/\b(\d{4})\b/);
        return match ? Number(match[1]) : null;
      })
      .filter((y) => Number.isFinite(y));

    return years.length > 0 ? Math.max(...years) : new Date().getFullYear();
  })();

  const parseLastUsedToMillis = (value, fallbackYear = defaultYearForTimestampParse) => {
    if (!value) return 0;
    const text = String(value).trim();

    const slashDate = text.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2}|\d{4}))?(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (slashDate) {
      const [, dayRaw, monthRaw, yearRaw, hour = '0', minute = '0', second = '0'] = slashDate;
      const day = Number(dayRaw);
      const month = Number(monthRaw);
      const year = yearRaw
        ? (yearRaw.length === 2 ? 2000 + Number(yearRaw) : Number(yearRaw))
        : fallbackYear;

      if (month < 1 || month > 12 || day < 1 || day > 31) return 0;

      return new Date(
        year,
        month - 1,
        day,
        Number(hour),
        Number(minute),
        Number(second)
      ).getTime();
    }

    const nativeParsed = Date.parse(text);
    return Number.isNaN(nativeParsed) ? 0 : nativeParsed;
  };

  const latestLastUsedByChatbot = students.reduce((acc, student) => {
    const chatbots = student?.chatbots || {};
    Object.entries(chatbots).forEach(([chatbotTitle, chatbotData]) => {
      const rawLastUsed = chatbotData?.last_used;
      const parsed = parseLastUsedToMillis(rawLastUsed);
      if (!rawLastUsed) return;

      const currentBest = acc[chatbotTitle];
      if (!currentBest || parsed > currentBest.timestamp) {
        acc[chatbotTitle] = {
          value: rawLastUsed,
          timestamp: parsed,
        };
      }
    });
    return acc;
  }, {});

  const allChatbotsWithDates = Object.entries(latestLastUsedByChatbot)
    .map(([chatbotTitle, usage]) => {
      return {
        chatbotTitle,
        chatbotName: getChatbotDisplayName(chatbotTitle),
        chatbotLastUsed: usage?.value || '',
        chatbotLastUsedTs: usage?.timestamp || 0,
      };
    })
    .sort((a, b) => {
      if ((b.chatbotLastUsedTs || 0) !== (a.chatbotLastUsedTs || 0)) {
        return (b.chatbotLastUsedTs || 0) - (a.chatbotLastUsedTs || 0);
      }
      return (a.chatbotName || '').localeCompare((b.chatbotName || ''), undefined, { numeric: true, sensitivity: 'base' });
    });

  const formatDateToMMDDYYYY = (dateText, fallbackYear = null) => {
    if (!dateText) return '';

    const withYear = String(dateText).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (withYear) {
      const [, day, month, year] = withYear;
      return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
    }

    const dayMonthOnly = String(dateText).match(/^(\d{1,2})\/(\d{1,2})$/);
    if (dayMonthOnly) {
      const [, day, month] = dayMonthOnly;
      if (!fallbackYear) {
        return `${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
      }
      return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${fallbackYear}`;
    }

    return String(dateText);
  };

  const formatLastEdited = (lastUsedValue) => {
    if (!lastUsedValue) return '';
    const parts = String(lastUsedValue).trim().split(/\s+/);
    const rawDatePart = parts[0] || '';
    const formattedDate = formatDateToMMDDYYYY(rawDatePart);
    let timePart = '';
    if (parts.length > 1) {
      const firstToken = parts[1] || '';
      const match = firstToken.match(/^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/);
      if (match) {
        const [, hh, mm] = match;
        timePart = `${hh.padStart(2, '0')}:${mm}`;
      }
    }
    return timePart ? `${formattedDate} ${timePart}` : formattedDate;
  };

  useEffect(() => {
    const container = topSelectionListRef.current;
    if (!container) return;

    if (displayOrder === 'chatbots') return;

    const selectedKey = displayOrder === 'chatbots'
      ? (selectedChatbotTitle ? `chatbot:${selectedChatbotTitle}` : '')
      : (selectedStudent?.key ? `student:${selectedStudent.key}` : '');

    if (!selectedKey) return;

    const selectedNode = selectionItemRefs.current[selectedKey];
    if (!selectedNode) return;

    const targetScrollTop = selectedNode.offsetTop - (container.clientHeight / 2) + (selectedNode.clientHeight / 2);
    container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
  }, [displayOrder, isTopSelectionCollapsed, selectedStudent, selectedChatbotTitle, students, allChatbotsWithDates]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        backgroundColor: 'background.default', 
        minHeight: '100vh',
        color: 'text.primary'
      }}>
        <Container maxWidth="md" sx={{ py: 4, backgroundColor: 'transparent' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" gutterBottom sx={{ color: 'text.primary', mb: 0 }}>
              {isLookupOnlyView ? 'Direct Student Transcript Lookup' : 'Student Transcripts'}
            </Typography>
          </Box>

          {!isLookupOnlyView && (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 4, alignItems: 'center' }}>
                <TextField
                  label="Professor Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing && e.keyCode !== 229) {
                      handleEmailSubmit();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleEmailSubmit}
                  disabled={isLoading}
                  sx={{
                    minWidth: '120px',
                    backgroundColor: isLoading ? 'grey' : undefined,
                    '&.Mui-disabled': {
                      backgroundColor: 'grey',
                      color: 'white',
                    }
                  }}
                >
                  {'Load Students'}
                </Button>
              </Box>

              {errorMessage && (
                <Typography color="error" sx={{ mb: 2 }}>
                  {errorMessage}
                </Typography>
              )}
            </>
          )}

          {isLookupOnlyView && (
            <Card sx={{ mb: 4, backgroundColor: 'background.paper' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
                  Enter a student email to load their transcripts directly.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                  <TextField
                    label="Student Email"
                    value={studentTranscriptEmail}
                    onChange={(e) => setStudentTranscriptEmail(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.nativeEvent.isComposing && e.keyCode !== 229) {
                        handleStudentTranscriptLookup();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleStudentTranscriptLookup}
                    disabled={isTranscriptLookupLoading}
                    sx={{ minWidth: '180px' }}
                  >
                    Load Conversations
                  </Button>
                </Box>

                {isTranscriptLookupLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {!isTranscriptLookupLoading && studentTranscriptSummaries.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 1 }}>
                      Conversations Found: {studentTranscriptSummaries.length}
                    </Typography>
                    <List sx={{ width: '100%' }}>
                      {studentTranscriptSummaries.map((item) => (
                        <StyledListItem
                          key={item.user_token}
                          button
                          onClick={() => handleTranscriptConversationOpen(item)}
                          selected={selectedTranscriptMeta?.userToken === item.user_token}
                        >
                          <ListItemText
                            primary={
                              <>
                                <Box component="strong" sx={{ fontWeight: 700 }}>
                                  {getChatbotDisplayName(item.chatbot_title) || 'Unknown Chatbot'}
                                </Box>
                                {` — Token: ${item.user_token}`}
                              </>
                            }
                            secondary={
                              <>
                                {`Updated: ${item.timestamp || 'Unknown'} | `}
                                <Box component="span" sx={{ color: 'error.main' }}>
                                  Interactions
                                </Box>
                                {`: ${item.conversation_length || 0}`}
                              </>
                            }
                            primaryTypographyProps={{ color: 'text.primary' }}
                            secondaryTypographyProps={{ color: 'text.secondary' }}
                          />
                        </StyledListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {!isTranscriptLookupLoading && studentTranscriptEmail && studentTranscriptSummaries.length === 0 && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No transcripts found for this student.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {!isLookupOnlyView && students.length > 0 && (
            <Card sx={{ mb: 4, backgroundColor: 'background.paper' }}>
              <CardContent>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="h6" sx={{ color: 'text.primary', flex: 1 }}>
                    {displayOrder === 'chatbots' ? 'Chatbots' : 'Students'}
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="display-by">Display by</InputLabel>
                    <Select
                      labelId="display-by"
                      id="display-by"
                      value={displayOrder}
                      label="Display by"
                      onChange={handleDisplayOrderChange}
                    >
                      <MenuItem value="default">Students</MenuItem>
                      <MenuItem value="chatbots">Chatbots</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box ref={topSelectionListRef} sx={{ maxHeight: isTopSelectionCollapsed ? 240 : 480, overflowY: 'auto' }}>
                <List>
                  {displayOrder === 'chatbots'
                    ? allChatbotsWithDates.map((chatbot) => {
                          const chatbotTitle = chatbot.chatbotTitle;
                          const displayLevel = getChatbotDisplayLevel(chatbotTitle);
                          return (
                            <StyledListItem
                              key={chatbotTitle}
                              ref={(node) => {
                                selectionItemRefs.current[`chatbot:${chatbotTitle}`] = node;
                              }}
                              button
                              onClick={() => handleChatbotModeClick(chatbotTitle)}
                              selected={selectedChatbotTitle === chatbotTitle}
                            >
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                      {displayLevel ? (
                                        <Typography component="span" sx={{ fontWeight: 'bold', mr: 1.25 }}>
                                          {displayLevel}
                                        </Typography>
                                      ) : null}
                                      <Typography component="span">{chatbot.chatbotName}</Typography>
                                    </Box>
                                    {chatbot.chatbotLastUsed ? (
                                      <Typography variant="caption" sx={{ color: 'text.secondary', ml: 2 }}>
                                        {formatLastEdited(chatbot.chatbotLastUsed)}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                }
                                primaryTypographyProps={{ color: 'text.primary' }}
                                secondaryTypographyProps={{ color: 'text.secondary' }}
                              />
                            </StyledListItem>
                          );
                        })
                    : students.map((student, i) => (
                        <StyledListItem
                          key={student.key || i}
                          ref={(node) => {
                            if (student.key) {
                              selectionItemRefs.current[`student:${student.key}`] = node;
                            }
                          }}
                          button
                          onClick={() => handleStudentClick(student)}
                          selected={selectedStudent && selectedStudent.key === student.key}
                        >
                          <ListItemText
                            primary={`${student.name} (${student.email})`}
                            primaryTypographyProps={{
                              fontWeight: selectedStudent && selectedStudent.key === student.key ? 'bold' : 'medium',
                              color: 'text.primary'
                            }}
                          />
                        </StyledListItem>
                      ))}
                </List>
                </Box>
                {(displayOrder === 'chatbots' ? allChatbotsWithDates.length > 5 : students.length > 5) && (
                  <Box sx={{ width: '100%', mt: 1 }}>
                    <Button
                      size="large"
                      fullWidth
                      onClick={() => setIsTopSelectionCollapsed((prev) => !prev)}
                      aria-label={isTopSelectionCollapsed ? 'Expand selection list' : 'Collapse selection list'}
                      sx={{
                        py: 1.25,
                        border: 1,
                        borderColor: 'divider',
                      }}
                      startIcon={
                        <ArrowDropDownIcon
                          sx={{
                            transform: isTopSelectionCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                            transition: 'transform 0.2s ease',
                          }}
                        />
                      }
                    >
                      {isTopSelectionCollapsed ? 'Expand' : 'Collapse'}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {selectedStudent && !selectedChatbot && !errorMessage && (
            <Card sx={{ mb: 4, backgroundColor: 'background.paper' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                  Chatbots Used by {selectedStudent.name}
                </Typography>
                <Box>
                  {(() => {
                    const inferDefaultYear = () => {
                      const years = Object.values(selectedStudent.chatbots || {})
                        .map((chatbot) => {
                          const raw = String(chatbot?.last_used || '');
                          const match = raw.match(/\b(\d{4})\b/);
                          return match ? Number(match[1]) : null;
                        })
                        .filter((y) => Number.isFinite(y));

                      if (years.length > 0) return Math.max(...years);
                      return new Date().getFullYear();
                    };

                    const defaultYear = inferDefaultYear();

                    const parseLastUsed = (value) => {
                      if (!value) return 0;
                      const text = String(value).trim();

                      // Backend timestamps are stored as DD/MM[/YYYY] with optional time.
                      const slashDate = text.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2}|\d{4}))?(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
                      if (slashDate) {
                        const [, dayRaw, monthRaw, yearRaw, hour = '0', minute = '0', second = '0'] = slashDate;
                        const day = Number(dayRaw);
                        const month = Number(monthRaw);
                        const year = yearRaw
                          ? (yearRaw.length === 2 ? 2000 + Number(yearRaw) : Number(yearRaw))
                          : defaultYear;

                        if (month < 1 || month > 12 || day < 1 || day > 31) return 0;

                        return new Date(
                          year,
                          month - 1,
                          day,
                          Number(hour),
                          Number(minute),
                          Number(second)
                        ).getTime();
                      }

                      // Fall back for ISO-like timestamps.
                      const nativeParsed = Date.parse(text);
                      if (!Number.isNaN(nativeParsed)) return nativeParsed;

                      return 0;
                    };

                    const sortedChatbots = Object.entries(selectedStudent.chatbots || {}).sort((a, b) => {
                      const timeA = parseLastUsed(a[1]?.last_used);
                      const timeB = parseLastUsed(b[1]?.last_used);

                      if (timeB !== timeA) return timeB - timeA;

                      return getChatbotDisplayName(a[0]).localeCompare(getChatbotDisplayName(b[0]));
                    });

                    return (
                      <List sx={{ width: '100%' }}>
                        {sortedChatbots.map(([chatbotTitle, chatbot]) => {
                          const displayName = getChatbotDisplayName(chatbotTitle);
                          const displayLevel = getChatbotDisplayLevel(chatbotTitle);
                          const chatConversation = chatbotConversations[chatbotTitle] || [];
                          return (
                            <StyledListItem
                              key={chatbotTitle}
                              button
                              onClick={() => handleChatbotClick(chatbotTitle)}
                              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            >
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                      {displayLevel ? (
                                        <Typography component="span" sx={{ fontWeight: 'bold', mr: 1.25 }}>
                                          {displayLevel}
                                        </Typography>
                                      ) : null}
                                      <Typography component="span">{displayName}</Typography>
                                    </Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'error.main',
                                        fontWeight: 'bold',
                                        ml: 2,
                                      }}
                                    >
                                      Interactions: {chatConversation.length}
                                      {chatbot?.last_used ? (
                                        <Box component="span" sx={{ ml: 1.5, color: 'text.secondary', fontWeight: 'normal' }}>
                                          {formatLastEdited(chatbot.last_used)}
                                        </Box>
                                      ) : null}
                                    </Typography>
                                  </Box>
                                }
                                primaryTypographyProps={{
                                  color: 'text.primary'
                                }}
                              />
                            </StyledListItem>
                          );
                        })}
                      </List>
                    );
                  })()}
                </Box>
              </CardContent>
            </Card>
          )}

          {displayOrder === 'chatbots' && selectedChatbotTitle && chatbotStudentHistories.length > 0 && !errorMessage && (
            <Card sx={{ backgroundColor: 'background.paper' }}>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 'bold', color: 'error.main' }}
                >
                  {getChatbotDisplayName(selectedChatbotTitle)}
                </Typography>
                {chatbotStudentHistories.map((entry) => {
                  const yearForStudent = getYearFromLastEdited(entry.lastUsed);
                  const isExpanded = Boolean(expandedStudentPanels[entry.studentKey]);
                  return (
                    <Box
                      key={entry.studentKey}
                      sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: '8px', overflow: 'hidden' }}
                    >
                      <Box
                        onClick={handleStudentPanelToggle(entry.studentKey)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 2,
                          py: 1.5,
                          backgroundColor: 'action.hover',
                          cursor: 'pointer',
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                          {entry.studentName} ({entry.studentEmail})
                        </Typography>
                        <ArrowDropDownIcon
                          sx={{
                            color: 'text.secondary',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                          }}
                        />
                      </Box>
                      {isExpanded && (
                        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                          {entry.conversation.length > 0 ? (
                            entry.conversation.map((msg, i) => (
                              <Box
                                key={`${entry.studentKey}-${i}`}
                                sx={{
                                  mb: 2,
                                  p: 2,
                                  borderRadius: '8px',
                                  border: 1,
                                  borderColor: 'divider',
                                  backgroundColor: 'action.hover',
                                  color: 'text.primary',
                                }}
                              >
                                {msg.timestamp && (
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                                    <Typography variant="caption" sx={{ color: (t) => t.palette.primary.main }}>
                                      {(() => {
                                        const ts = msg.timestamp || '';
                                        const parts = ts.split(' ');
                                        const rawDatePart = parts.length > 0 ? parts[0] : ts;
                                        const datePart = formatDateToMMDDYYYY(rawDatePart, yearForStudent);
                                        let timePart = '';
                                        if (parts.length > 1) {
                                          const t = parts.slice(1).join(' ').trim();
                                          const firstToken = t.split(' ')[0];
                                          const timePieces = firstToken.split(':');
                                          if (timePieces.length >= 2) {
                                            timePart = `${timePieces[0]}:${timePieces[1]}`;
                                          } else {
                                            timePart = firstToken;
                                          }
                                        }
                                        return (
                                          <>
                                            <Box component="span" sx={{ fontWeight: 'bold', color: (t) => t.palette.primary.dark, fontSize: '1rem' }}>{datePart}</Box>
                                            {timePart && (
                                              <Box component="span" sx={{ ml: 1, color: (t) => t.palette.primary.main, fontSize: '1rem' }}>{timePart}</Box>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </Typography>
                                  </Box>
                                )}

                                <Typography variant="body2" component="div" sx={{ color: 'inherit' }}>
                                  <strong>User:</strong> {msg.user}
                                </Typography>
                                {msg.audio_urls && msg.audio_urls.length > 0 && (
                                  <AudioPlayer audioUrls={msg.audio_urls} />
                                )}
                                <Typography variant="body2" component="div" sx={{ mt: 1, color: 'inherit' }}>
                                  <strong>Assistant:</strong> {msg.assistant}
                                </Typography>
                              </Box>
                            ))
                          ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                              No conversation found.
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {selectedChatbot && conversation.length > 0 && (
            <Card sx={{ backgroundColor: 'background.paper' }}>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 'bold', color: 'error.main' }}
                >
                  {selectedChatbot}
                </Typography>
                {conversation.map((msg, i) => (
                  <Box 
                    key={i} 
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      borderRadius: '8px',
                      border: 1,
                      borderColor: 'divider',
                      backgroundColor: 'action.hover',
                      color: 'text.primary',
                    }}
                  >
                    {/* Timestamp line (small, muted) */}
                    {msg.timestamp && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                          <Typography variant="caption" sx={{ color: (t) => t.palette.primary.main }}>
                            {(() => {
                              const ts = msg.timestamp || '';
                              const parts = ts.split(' ');
                              const rawDatePart = parts.length > 0 ? parts[0] : ts;
                              const datePart = formatDateToMMDDYYYY(rawDatePart, selectedChatbotYear);
                              let timePart = '';
                              if (parts.length > 1) {
                                // Join remaining parts and extract first token (usually HH:MM:SS), then drop seconds
                                const t = parts.slice(1).join(' ').trim();
                                const firstToken = t.split(' ')[0];
                                const timePieces = firstToken.split(':');
                                if (timePieces.length >= 2) {
                                  timePart = `${timePieces[0]}:${timePieces[1]}`;
                                } else {
                                  timePart = firstToken;
                                }
                              }
                              return (
                                <>
                                  <Box component="span" sx={{ fontWeight: 'bold', color: (t) => t.palette.primary.dark, fontSize: '1rem' }}>{datePart}</Box>
                                  {timePart && (
                                    <Box component="span" sx={{ ml: 1, color: (t) => t.palette.primary.main, fontSize: '1rem' }}>{timePart}</Box>
                                  )}
                                </>
                              );
                            })()}
                        </Typography>
                      </Box>
                    )}

                    <Typography variant="body2" component="div" sx={{ color: 'inherit' }}>
                      <strong>User:</strong> {msg.user}
                    </Typography>
                    {/* Add the AudioPlayer component here */}
                    {msg.audio_urls && msg.audio_urls.length > 0 && (
                        <AudioPlayer audioUrls={msg.audio_urls} />
                    )}
                    <Typography variant="body2" component="div" sx={{ mt: 1, color: 'inherit' }}>
                      <strong>Assistant:</strong> {msg.assistant}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          {selectedTranscriptMeta && (
            <Card sx={{ mt: 4, backgroundColor: 'background.paper' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'error.main' }}>
                  Transcript: {selectedTranscriptMeta.studentEmail}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Chatbot: {getChatbotDisplayName(selectedTranscriptMeta.chatbotTitle) || 'Unknown'} | Token: {selectedTranscriptMeta.userToken} | Updated: {selectedTranscriptMeta.timestamp || 'Unknown'}
                </Typography>

                {selectedTranscriptConversation.length > 0 ? (
                  selectedTranscriptConversation.map((msg, i) => (
                    <Box
                      key={`${selectedTranscriptMeta.userToken}-${i}`}
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: '8px',
                        border: 1,
                        borderColor: 'divider',
                        backgroundColor: 'action.hover',
                        color: 'text.primary',
                      }}
                    >
                      {msg.timestamp && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                          <Typography variant="caption" sx={{ color: (t) => t.palette.primary.main }}>
                            {msg.timestamp}
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="body2" component="div" sx={{ color: 'inherit' }}>
                        <strong>User:</strong> {msg.user}
                      </Typography>
                      {msg.audio_urls && msg.audio_urls.length > 0 && (
                        <AudioPlayer audioUrls={msg.audio_urls} />
                      )}
                      <Typography variant="body2" component="div" sx={{ mt: 1, color: 'inherit' }}>
                        <strong>Assistant:</strong> {msg.assistant}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No conversation content found.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

ProfessorView.propTypes = {
  themeMode: PropTypes.string,
  onThemeChange: PropTypes.func,
};

export default ProfessorView;
